import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AppConstants } from '../../../app.constants';
import { AppConfig } from '../../../app.settings';
import { ViewMode } from '../../enums/view-mode.enum';
import { ResetMonitorImageResolutionViewModel } from '../../models/view-models/monitor-models/reset-monitor-image-resolution.view-model';
import { BehaviorScreen } from '../../models/view-models/monitor-models/screen.type';
import { UpdateMonitorImageResolutionViewModel } from '../../models/view-models/monitor-models/update-monitor-image-resolution.view-model';
import { FullScreenMode } from '../../models/view-models/monitor-models/full-screen-mode.model';

@Injectable({
  providedIn: 'root'
})
export class MonitorChangeViewService {

  public selectedForFullScreenChanged: BehaviorSubject<BehaviorScreen> = new BehaviorSubject(null);
  public fullScreenMonitor: BehaviorScreen = null;
  public isMonitorConnected: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public monitorsViewChange: Subject<ViewMode> = new Subject();
  private endpoint: string;

  constructor(
    private http: HttpClient,
    private config: AppConfig
  ) {
    this.endpoint = `${this.config.getConfig('api')}Monitor`;
  }

  changeImageResolution(
    monitorModel: UpdateMonitorImageResolutionViewModel,
    isToDecreaseResolutionForScreensExceptFullScreen: boolean = true,
    anonymous: boolean = false
  ): Observable<void> {
    const endpoint = this.endpoint + `/change-image-resolution?isToDecreaseResolutionForScreensExceptFullScreen=${isToDecreaseResolutionForScreensExceptFullScreen}`;
    return this.http.put<void>(endpoint, monitorModel, this.getRequestConfig(anonymous));
  }

  resetImageResolution(modelToReset: ResetMonitorImageResolutionViewModel[]): Observable<void> {
    const endpoint = this.endpoint + '/reset-image-resolution';
    return this.http.put<void>(endpoint, modelToReset, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  public initChangeImageModel(monitor: BehaviorScreen, isFullScreenMode: boolean = false): void {
    if (isFullScreenMode) {
      if (this.fullScreenMonitor) {
        const resetImageResolutionModel = new ResetMonitorImageResolutionViewModel(this.fullScreenMonitor);
        this.resetImageResolution([resetImageResolutionModel]).subscribe();
      }

      monitor.PictureURL = null;
      this.fullScreenMonitor = monitor;
      const imageToFullResolutionModel: UpdateMonitorImageResolutionViewModel =
        this.generateMonitorImageResolutionViewModel(monitor, isFullScreenMode);
      this.changeImageResolution(imageToFullResolutionModel).subscribe();
    } else {
      const resetImageResolutionModel = new ResetMonitorImageResolutionViewModel(monitor);
      this.resetImageResolution([resetImageResolutionModel]).subscribe();
    }
  }

  public informServerMonitorConnectedToWebMonitorGroup(machineAddresses: string[], isTriggeredOnDashboard = false): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('isFromDashboard', isTriggeredOnDashboard);
    return this.http.post<void>(`${this.endpoint}/connect-to-web-monitor-group`, machineAddresses, { params });
  }

  public setFullScreenMode(model: FullScreenMode, anonymous: boolean = false): Observable<void> {
    const endpoint = this.endpoint + '/set-fullscreen-parameters';
    return this.http.put<void>(endpoint, model, this.getRequestConfig(anonymous));
  }

  public getSnapshot(imageSrc: string, anonymous: boolean = false): Observable<Blob> {
    return this.http.get(imageSrc, { responseType: 'blob', ...this.getRequestConfig(anonymous) });
  }

  public isAndroidMonitorSwitchedToFullScreenMode(handler: string): boolean {
    return this.fullScreenMonitor?.MonitorHandler === handler
      && this.fullScreenMonitor.Machine.IsAndroid
      && !this.fullScreenMonitor.PictureURL;
  }

  private generateMonitorImageResolutionViewModel(monitor: BehaviorScreen, isFullScreenMode: boolean = false)
    : UpdateMonitorImageResolutionViewModel {
    let imageModel: UpdateMonitorImageResolutionViewModel = new UpdateMonitorImageResolutionViewModel(monitor);
    if (isFullScreenMode) {
      imageModel = new UpdateMonitorImageResolutionViewModel(monitor,
        AppConstants.FULL_SCREEN_MONITOR_IMAGE_HEIGHT, AppConstants.FULL_SCREEN_MONITOR_IMAGE_WIDTH);
    }

    return imageModel;
  }

  private getRequestConfig(anonymous: boolean): { headers?: HttpHeaders } {
    return anonymous
      ? { headers: new HttpHeaders({ 'No-Auth': 'True' }) }
      : {};
  }
}
